import { Link } from "react-router-dom";
import { Zap, BookOpen, Beaker, Globe, Dumbbell, Landmark, Lightbulb, Music, Utensils, Plane, Code, TrendingUp, Heart } from "lucide-react";
import { SEOHead, itemListJsonLd } from "@/components/seo/SEOHead.tsx";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

const USE_CASES = [
  {
    icon: Landmark,
    title: "History & Documentary",
    description: "Turn historical events into cinematic narrated videos. Ancient civilizations, battles, biographies — the AI handles research-style scripts while you control the storytelling.",
    keywords: "history channel, documentary, historical facts",
    example: "\"The Fall of the Roman Empire — What Really Happened\"",
    format: "long_form",
  },
  {
    icon: Beaker,
    title: "Science & Education",
    description: "Explain complex topics visually. Physics, biology, space science — AI generates clear scripts and matching illustrations for each concept.",
    keywords: "educational videos, science explained, learning",
    example: "\"Why Is the Sky Blue? The Science of Light Scattering\"",
    format: "long_form",
  },
  {
    icon: TrendingUp,
    title: "Finance & Investing",
    description: "Market explainers, investing guides, and economic breakdowns. AI adapts tone from casual to authoritative depending on your audience.",
    keywords: "investing tips, stock market, personal finance",
    example: "\"Index Funds vs ETFs — Which Should You Buy?\"",
    format: "long_form",
  },
  {
    icon: BookOpen,
    title: "Book Summaries & Reviews",
    description: "Summarize bestsellers in 5–10 minute videos. The two-gate workflow lets you verify the summary accuracy before any visuals are generated.",
    keywords: "book summary, book review, reading list",
    example: "\"Atomic Habits in 8 Minutes — Key Takeaways\"",
    format: "long_form",
  },
  {
    icon: Lightbulb,
    title: "Motivational & Self-Help",
    description: "Inspirational content with AI-generated imagery that matches the emotional arc of each script section. Great for Shorts and long-form.",
    keywords: "motivation, self improvement, mindset",
    example: "\"5 Habits That Changed My Life\"",
    format: "shorts",
  },
  {
    icon: Globe,
    title: "Geography & Travel",
    description: "Virtual tours, country profiles, and \"top 10 places\" videos. AI generates location-style imagery for each segment.",
    keywords: "travel video, geography, countries explained",
    example: "\"10 Places You Won't Believe Exist\"",
    format: "long_form",
  },
  {
    icon: Code,
    title: "Tech Explainers",
    description: "Explain how technologies work, compare products, or break down industry news. Great for YouTube Shorts tech tips.",
    keywords: "tech explained, how it works, AI tools",
    example: "\"How Does Wi-Fi Actually Work?\"",
    format: "shorts",
  },
  {
    icon: Heart,
    title: "Health & Wellness",
    description: "Nutrition guides, mental health topics, and exercise science — all with AI-generated visuals that match each talking point.",
    keywords: "health tips, wellness, nutrition",
    example: "\"What Happens to Your Body When You Stop Eating Sugar\"",
    format: "long_form",
  },
  {
    icon: Music,
    title: "Music & Pop Culture",
    description: "Artist deep-dives, genre histories, and trend analysis. AI handles the research-heavy scripting; you refine the angle.",
    keywords: "music history, pop culture, artist biography",
    example: "\"The Rise and Fall of Disco\"",
    format: "long_form",
  },
  {
    icon: Utensils,
    title: "Food & Cooking Facts",
    description: "Food science, cuisine histories, and \"what you didn't know\" food content. Not recipe tutorials — think edutainment.",
    keywords: "food facts, cooking science, cuisine history",
    example: "\"Why Does Cutting Onions Make You Cry?\"",
    format: "shorts",
  },
  {
    icon: Dumbbell,
    title: "Fitness & Sports",
    description: "Exercise science, athlete profiles, and sport breakdowns. Shorts work great for quick fitness tips.",
    keywords: "fitness tips, sports science, workout",
    example: "\"The Science Behind Muscle Growth\"",
    format: "long_form",
  },
  {
    icon: Plane,
    title: "Mysteries & Unexplained",
    description: "Unsolved mysteries, true crime summaries, and conspiracy breakdowns. The script review gate lets you fact-check before publishing.",
    keywords: "mystery, unsolved, true crime, unexplained",
    example: "\"5 Disappearances That Still Have No Explanation\"",
    format: "long_form",
  },
];

export function UseCasesPage() {
  return (
    <>
      <SEOHead
        title="Faceless YouTube Channel Ideas & Use Cases"
        description="Faceless YouTube channel ideas for TubeForge: history, finance, science, book summaries, motivation, tech, health, and more. See examples and get started."
        path="/use-cases"
        jsonLd={itemListJsonLd(USE_CASES.map((uc) => ({
          name: uc.title,
          url: `https://tubeforge.io/use-cases#${uc.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
          description: uc.description,
        })))}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            Use cases
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold leading-tight mb-5"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            What kind of channel can you build?
          </h1>
          <p className="text-[16px] md:text-[18px] leading-relaxed max-w-xl mx-auto" style={{ color: T.inkSoft }}>
            TubeForge works for any niche where narrated, image-based videos perform —
            and that's a lot of niches. Here are the most popular ones.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 md:py-16" style={{ backgroundColor: T.paper2 }}>
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {USE_CASES.map((uc) => {
              const Icon = uc.icon;
              return (
                <div
                  key={uc.title}
                  className="rounded-2xl p-6 flex flex-col"
                  style={{ backgroundColor: "#fff", border: `1px solid ${T.rule}` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(255,91,31,0.08)" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: T.accent }} />
                    </div>
                    <div>
                      <h3 className="text-[15px] font-bold">{uc.title}</h3>
                      <span className="text-[11px] font-mono" style={{ color: T.inkSoft }}>
                        {uc.format === "shorts" ? "Shorts · 9:16" : "Long-form · 16:9"}
                      </span>
                    </div>
                  </div>
                  <p className="text-[13px] leading-relaxed flex-1 mb-4" style={{ color: T.inkSoft }}>
                    {uc.description}
                  </p>
                  <div
                    className="text-[12px] px-3 py-2 rounded-lg mb-3"
                    style={{ backgroundColor: T.paper, border: `1px solid ${T.rule}`, fontStyle: "italic", color: T.ink }}
                  >
                    Example: {uc.example}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {uc.keywords.split(", ").map((kw) => (
                      <span
                        key={kw}
                        className="text-[10px] font-mono px-2 py-0.5 rounded"
                        style={{ backgroundColor: T.paper2, color: T.inkSoft }}
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-2xl px-5">
          <h2
            className="text-2xl md:text-3xl font-bold mb-4"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Don't see your niche?
          </h2>
          <p className="text-[15px] mb-8" style={{ color: T.inkSoft }}>
            If it can be told with narration and images, TubeForge can build it.
            Start with a free trial — bring your own topic.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-[15px] font-semibold px-7 py-3.5 rounded-xl transition-opacity hover:opacity-90"
            style={{ backgroundColor: T.accent, color: "#fff" }}
          >
            <Zap className="w-4 h-4" /> Start free trial
          </Link>
        </div>
      </section>
    </>
  );
}
